import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Coins, Wallet, TrendingUp, Loader2, ArrowRight, Plus, Banknote, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StakeholderDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [me, setMe] = useState(null);
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(500);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, o] = await Promise.all([
        axios.get(`${API}/stakeholders/me`, { headers }),
        axios.get(`${API}/stakeholders/offerings`, { headers }),
      ]);
      setMe(m.data);
      setOfferings(o.data.offerings || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Confirm top-up if returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('topup_success') && sessionId && token) {
      axios.get(`${API}/stakeholders/wallet/topup/confirm?session_id=${sessionId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          if (r.data.credited) toast.success(`Wallet credited $${(r.data.amount_cents / 100).toLocaleString()}`);
          else if (r.data.already_credited) toast.info('Already credited');
          window.history.replaceState({}, '', '/stakeholder/dashboard');
          load();
        })
        .catch(() => {});
    }
    if (params.get('connect') === 'done') {
      toast.success('Stripe Connect complete — you can now withdraw to your bank');
      window.history.replaceState({}, '', '/stakeholder/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startTopup = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/stakeholders/wallet/topup`, { amount_cents: Math.round(topupAmount * 100) }, { headers });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Top-up failed'); setSubmitting(false); }
  };

  const startConnectOnboard = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/stakeholders/wallet/connect/onboard`, {}, { headers });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setSubmitting(false); }
  };

  const doWithdraw = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/stakeholders/wallet/withdraw`, { amount_cents: Math.round(withdrawAmount * 100) }, { headers });
      toast.success(`Withdrew $${(r.data.amount_cents / 100).toLocaleString()} to your bank`);
      setShowWithdraw(false);
      setWithdrawAmount(0);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Withdraw failed'); }
    setSubmitting(false);
  };

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) return <div className="min-h-screen bg-background pt-20 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>;

  const wallet = me?.wallet || {};
  const holdings = me?.holdings || [];
  const totalHoldings = holdings.length;
  const totalCost = holdings.reduce((s, h) => s + (h.price_paid_cents || 0), 0);
  const balance = wallet.balance_cents || 0;
  const hasConnect = !!wallet.stripe_connect_id;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="stakeholder-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2 font-['Space_Grotesk']">
              <Coins className="w-7 h-7 text-fuchsia-400" /> Stakeholder Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Your wallet, holdings, and live offerings.</p>
          </div>
        </div>

        {/* Wallet stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <Card><CardContent className="p-4"><div className="text-[10px] uppercase text-muted-foreground">Wallet balance</div><div className="text-2xl font-bold mt-1 text-emerald-400" data-testid="wallet-balance">{fmt(wallet.balance_cents)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-[10px] uppercase text-muted-foreground">Active holdings</div><div className="text-2xl font-bold mt-1">{totalHoldings}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-[10px] uppercase text-muted-foreground">Capital deployed</div><div className="text-2xl font-bold mt-1">{fmt(totalCost)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-[10px] uppercase text-muted-foreground">Lifetime earned</div><div className="text-2xl font-bold mt-1">{fmt(wallet.lifetime_earned_cents)}</div></CardContent></Card>
        </div>

        {/* Wallet actions row */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button onClick={() => setShowTopup(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add funds
          </Button>
          {hasConnect ? (
            <Button onClick={() => { setWithdrawAmount(Math.floor(balance / 100)); setShowWithdraw(true); }} variant="outline" disabled={balance < 1000} data-testid="withdraw-btn">
              <Banknote className="w-4 h-4 mr-1.5" /> Withdraw to bank
            </Button>
          ) : (
            <Button onClick={startConnectOnboard} variant="outline" disabled={submitting} data-testid="connect-bank-btn">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Connect bank for withdrawals
            </Button>
          )}
        </div>

        {/* Open offerings */}
        <h2 className="text-xl font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-indigo-400" /> Available NFT Tickets</h2>
        {offerings.filter((o) => o.status === 'open').length === 0 ? (
          <Card className="mb-6"><CardContent className="p-8 text-center text-muted-foreground">No open offerings right now. Check back soon.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="open-offerings">
            {offerings.filter((o) => o.status === 'open').map((o) => (
              <Card key={o.offering_id} className="hover:border-fuchsia-500/40 cursor-pointer transition" onClick={() => navigate(`/stakeholder/event/${o.event_id}`)} data-testid={`offering-card-${o.event_id}`}>
                {o.event?.image_url && <img src={o.event.image_url} alt={o.event.title} className="w-full h-32 object-cover rounded-t-lg" />}
                <CardContent className="p-4">
                  <div className="font-semibold truncate">{o.event?.title || o.event_title}</div>
                  <div className="text-xs text-muted-foreground mb-2">{o.event?.city || ''}</div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current price</span>
                    <span className="font-bold text-fuchsia-300">{fmt(o.current_price_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{o.shares_sold} of {o.max_supply} NFT Tickets sold</span>
                    <span>{o.max_supply - o.shares_sold} left</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5 mt-2"><div className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 h-full rounded-full" style={{ width: `${Math.min(100, (o.shares_sold / Math.max(1, o.max_supply)) * 100)}%` }} /></div>
                  <div className="text-[10px] text-muted-foreground mt-1.5">
                    Profit split: {Math.round((1 - (o.eventism_reserve_pct ?? 0.30)) * 100)}% holders / {Math.round((o.eventism_reserve_pct ?? 0.30) * 100)}% Eventism
                  </div>
                  <Button size="sm" className="w-full mt-3 bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid={`view-offering-${o.event_id}`}>View trading dashboard <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Holdings */}
        <h2 className="text-xl font-bold mb-3 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-400" /> Your holdings</h2>
        {holdings.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No holdings yet. Pick an offering above to deploy capital.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="my-holdings">
            {holdings.map((h) => (
              <Card key={h.holding_id} className="hover:border-emerald-500/40 cursor-pointer transition" onClick={() => navigate(`/stakeholder/event/${h.event_id}`)}>
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{h.offering?.event_title || h.event_id}</div>
                    <div className="text-xs text-muted-foreground">Bought {new Date(h.bought_at).toLocaleDateString()} · Status {h.offering?.status}</div>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <div><div className="text-[10px] uppercase text-muted-foreground">Paid</div><div className="font-semibold">{fmt(h.price_paid_cents)}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Current</div><div className="font-semibold">{fmt(h.current_price_cents)}</div></div>
                    {h.payout_cents != null && <div><div className="text-[10px] uppercase text-muted-foreground">Payout</div><div className="font-semibold text-emerald-400">{fmt(h.payout_cents)}</div></div>}
                  </div>
                  <Badge variant="outline" className={h.offering?.status === 'settled' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : ''}>{h.offering?.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* TOPUP DIALOG */}
        <Dialog open={showTopup} onOpenChange={setShowTopup}>
          <DialogContent data-testid="topup-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-400" /> Add funds to your wallet</DialogTitle>
              <DialogDescription>Stripe processing fee is added on top so your wallet credit equals the exact amount you choose.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Amount (USD, min $50)</label>
                <Input type="number" min="50" step="50" value={topupAmount} onChange={(e) => setTopupAmount(Math.max(50, parseInt(e.target.value) || 0))} data-testid="topup-amount-input" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[500, 1000, 2500, 5000, 10000].map((v) => (
                  <Button key={v} size="sm" variant="outline" onClick={() => setTopupAmount(v)}>${v.toLocaleString()}</Button>
                ))}
              </div>
              <div className="rounded-md bg-slate-900/50 border border-border p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Wallet credit</span><span className="font-bold font-mono">{fmt(topupAmount * 100)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Stripe fee</span><span className="text-muted-foreground font-mono">{fmt(Math.round((topupAmount * 100 + 30) / 0.971) - topupAmount * 100)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">You pay today</span><span className="font-bold font-mono">{fmt(Math.round((topupAmount * 100 + 30) / 0.971))}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTopup(false)}>Cancel</Button>
              <Button onClick={startTopup} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-topup-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Continue to Stripe
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* WITHDRAW DIALOG */}
        <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
          <DialogContent data-testid="withdraw-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-400" /> Withdraw to bank</DialogTitle>
              <DialogDescription>Funds arrive in 0–2 business days via Stripe Connect.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Amount (USD, min $10)</label>
                <Input type="number" min="10" max={balance / 100} value={withdrawAmount} onChange={(e) => setWithdrawAmount(Math.max(10, Math.min(balance / 100, parseFloat(e.target.value) || 0)))} data-testid="withdraw-amount-input" />
              </div>
              <Button size="sm" variant="outline" onClick={() => setWithdrawAmount(Math.floor(balance / 100))}>Max — {fmt(balance)}</Button>
              <div className="rounded-md bg-slate-900/50 border border-border p-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">After withdrawal</span><span className="font-mono">{fmt(balance - Math.round(withdrawAmount * 100))}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWithdraw(false)}>Cancel</Button>
              <Button onClick={doWithdraw} disabled={submitting || withdrawAmount < 10} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-withdraw-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Withdraw {fmt(Math.round(withdrawAmount * 100))}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
